import { db, docsFromSnapshot } from "@/firebase";
import {
  RESOURCE_WORK_HOURS,
  RESOURCE_BREAK,
  RESOURCE_HOLIDAY,
  RESOURCE_SICK,
  RESOURCE_COMPASSIONATE_LEAVE
} from "@/helpers/eventTypes";
import _ from "lodash";
import { standardizeRosterTime } from "@/helpers/rosterTime";

export const rosterEventTypes = {
  RESOURCE_WORK_HOURS,
  RESOURCE_BREAK,
  RESOURCE_HOLIDAY,
  RESOURCE_SICK,
  RESOURCE_COMPASSIONATE_LEAVE
};

export const DEFAULT_ROSTER_EVENT = {
  tenantId: null,
  storeId: null,
  type: rosterEventTypes.RESOURCE_WORK_HOURS,
  resourceId: null,
  start: null,
  end: null,
  created: null,
  lastModified: null
};

const mutationTypes = {
  SET_ROSTER: "SET_ROSTER",
  SET_ROSTER_EVENT: "SET_ROSTER_EVENT",
  REMOVE_ROSTER_EVENT: "REMOVE_ROSTER_EVENT",
  SET_ROSTER_SUBSCRIPTION: "SET_ROSTER_SUBSCRIPTION"
};

const state = {
  roster: {},
  subscription: null
};

const getters = {
  roster: state => state.roster
};

function standardizeTime(time) {
  return standardizeRosterTime(time);
}



const actions = {
  async createEvent(context, event) {
    const ref = db.collection("events").doc();

    event = {
      ...event,
      _id: ref.id,
      start: standardizeTime(event.start),
      end: standardizeTime(event.end),
      tenantId: context.rootGetters.tenantId,
      storeId: _.get(context,"rootGetters.userClaims.storeId"),
      created: +new Date(),
      lastModified: +new Date()
    };

    await ref.set(event);

    context.commit(mutationTypes.SET_ROSTER_EVENT, event);
  },
  async updateEvent(context, event) {
    event.lastModified = +new Date();
    event = {...event,
      start: standardizeTime(event.start),
      end: standardizeTime(event.end)}
    await db
      .collection("events")
      .doc(event._id)
      .set(event);

    context.commit(mutationTypes.SET_ROSTER_EVENT, event);
  },
  async deleteEvent(context, event) {
    await db
      .collection("events")
      .doc(event._id)
      .delete();
    context.commit(mutationTypes.REMOVE_ROSTER_EVENT, event);
  },
  async applyRosterPlan(context, plan) {
    const deletes = (plan && plan.deletes) || [];
    const creates = (plan && plan.creates) || [];
    const tenantId = context.rootGetters.tenantId;
    const storeId = _.get(context, "rootGetters.userClaims.storeId");
    const now = +new Date();
    const ops = [];

    deletes.forEach(event => {
      if (event && event._id) ops.push({ kind: "delete", event });
    });
    creates.forEach(event => {
      if (event) ops.push({ kind: "create", event });
    });

    const chunkSize = 400;
    for (let i = 0; i < ops.length; i += chunkSize) {
      const slice = ops.slice(i, i + chunkSize);
      const batch = db.batch();
      const created = [];
      slice.forEach(op => {
        if (op.kind === "delete") {
          batch.delete(db.collection("events").doc(op.event._id));
          return;
        }
        const ref = db.collection("events").doc();
        const event = {
          ...op.event,
          _id: ref.id,
          start: standardizeTime(op.event.start),
          end: standardizeTime(op.event.end),
          tenantId,
          storeId,
          allDay: !!op.event.allDay,
          created: now,
          lastModified: now
        };
        batch.set(ref, event);
        created.push(event);
      });
      await batch.commit();
      slice.forEach(op => {
        if (op.kind === "delete") {
          context.commit(mutationTypes.REMOVE_ROSTER_EVENT, op.event);
        }
      });
      created.forEach(event => {
        context.commit(mutationTypes.SET_ROSTER_EVENT, event);
      });
    }
  },
  subscribeRoster(context) {
    if (context.state.subscription) return;

    const subscription = db
      .collection("events")
      .where("storeId", "==", context.rootGetters.userClaims.storeId)
      .where("type", "in", Object.values(rosterEventTypes))
      .onSnapshot(snap => {
        const roster = docsFromSnapshot(snap);
        context.commit(mutationTypes.SET_ROSTER, roster);
      });

    context.commit(mutationTypes.SET_ROSTER_SUBSCRIPTION, subscription);
  },
  unsubscribeRoster(context) {
    const unsubscribe = context.state.subscription;

    if (unsubscribe) {
      unsubscribe();
      context.commit(mutationTypes.SET_ROSTER_SUBSCRIPTION, null);
    }
  }
};

const mutations = {
  [mutationTypes.SET_ROSTER](state, roster) {
    state.roster = roster;
  },
  [mutationTypes.SET_ROSTER_SUBSCRIPTION](state, subscription) {
    state.subscription = subscription;
  },
  [mutationTypes.SET_ROSTER_EVENT](state, event) {
    state.roster = { ...state.roster, [event._id]: event };
  },
  [mutationTypes.REMOVE_ROSTER_EVENT](state, event) {
    const next = { ...state.roster };
    delete next[event._id];
    state.roster = next;
  }
};

export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
};
